import {createApp} from 'vue';
import App from './App/App.vue';
import router from './router';
import {env} from '@/config/env';

import {library} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';

import Toast, {PluginOptions} from 'vue-toastification';

import {ProfabricComponents} from '@profabric/vue-components';
import VueGtag from 'vue-gtag';
import * as Sentry from "@sentry/vue";
import {createSentryPiniaPlugin} from "@sentry/vue";
import {downgradeForbiddenToWarning} from "@/utils/sentry";
import packageJson from '../package.json';
import 'vue-toastification/dist/index.css'
import './index.scss';
import {faEnvelope, faLock} from '@fortawesome/free-solid-svg-icons';
import {createPinia} from 'pinia';
import DataTablesLib from 'datatables.net';
import DataTable from 'datatables.net-vue3';
import 'datatables.net-select';
import 'datatables.net-buttons';
import 'datatables.net-buttons/js/buttons.html5';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'

import axios from 'axios';
import { setupAxiosErrorInterceptor } from '@/utils/axiosErrorInterceptor';

import timeago from 'vue-timeago3'
import VueTippy from 'vue-tippy'
import 'tippy.js/dist/tippy.css'

declare global {
  interface Window {
    viewLocation: (locationID: string) => void
  }
}


// Get environment vairables (runtime-injected, falls back to build-time import.meta.env)
const {VITE_NODE_ENV, VITE_GA_ID, VITE_API_V1_URL, VITE_API_V1_PORT, VITE_SENTRY_DSN, VITE_SENTRY_ENVIRONMENT} = env;

// Set the options for timeago
const timeagoOptions = {
  converterOptions: {
      includeSeconds: false,
  }
}

// Make DataTable use the Datatables libraries
DataTable.use(DataTablesLib);

// Disable alert (Datatables will abuse it and since we use a Vue Wrapper we cannot modify the error mode)
window.alert = (val) => {
  console.error(val)
};

// Set Axios base url
const port = VITE_API_V1_PORT || "";
const formattedPort = (port && !port.startsWith(":")) ? `:${port}` : port;
const apiV1Url = VITE_API_V1_URL || "/api/v1/";
const apiBaseUrl = `${window.location.protocol}//${window.location.hostname}${formattedPort}${apiV1Url}`;
axios.defaults.baseURL = apiBaseUrl;
console.log(`[Frontend] API Base URL: ${axios.defaults.baseURL}`);
axios.defaults.withCredentials = true;

// We don't use this library in the main app but it is still here for some legacy pages
library.add(faEnvelope, faLock);

const getCSRFToken = () => {
    const match = document.cookie.match(/csrftoken(?:_\w+)?=([^;]+)/);
    return match ? match[1] : null;
};

axios.interceptors.request.use((config) => {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
});

// Setup the response error interceptor AFTER initialization of config
setupAxiosErrorInterceptor(axios);

const options: PluginOptions = {
    timeout: 5000,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: true,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: false,
    closeButton: 'button',
    icon: true,
    rtl: false
};

// Create the Vue App and add plugins
const app = createApp(App);
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());

window.viewLocation = (locationID: string) => {
  if (!locationID) return
  router.push(`/locations/${locationID}`)
}

app.component('font-awesome-icon', FontAwesomeIcon)
    .use(pinia)
    .use(router)
    .use(timeago,  timeagoOptions)
    .use(Toast, options)
    .use(VueTippy, {
        directive: 'tippy', // => v-tippy
        component: 'tippy', // => <tippy/>
    })
    .component('VueDatePicker', VueDatePicker)
    .use(ProfabricComponents)

// We currently do not use GTagging
if (VITE_NODE_ENV === 'production' && VITE_GA_ID) {
    app.use(
        VueGtag,
        {
            config: {id: VITE_GA_ID}
        },
        router
    );
}

Sentry.init({
  app,
  dsn: VITE_SENTRY_DSN,
  release: packageJson.version,
  environment: VITE_SENTRY_ENVIRONMENT || VITE_NODE_ENV,
  sendDefaultPii: true,
  beforeSend: downgradeForbiddenToWarning,
  integrations: [
    Sentry.vueIntegration({
      tracingOptions: {
          trackComponents: true,
      }
    }),
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    }),
    Sentry.feedbackIntegration({
      colorScheme: "light",
      autoInject: false,
      onFormOpen: () => {
        document.body.classList.add("sentry-feedback-open");
      },
      onFormClose: () => {
        document.body.classList.remove("sentry-feedback-open");
      },
    }),
  ],
  tracesSampleRate: 0.1,
  tracePropagationTargets: [
    /.*\.pollux\.net\/api\/.*/,
    /.*\.veducon\.local(:\d+)?\/api\/.*/,
  ],
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.2,
});


// We mount the app to the app id
app.mount('#app');
